import md5 from 'js-md5'

export class BrowserFingerprint {
  constructor() {
    // 缓存key（避免与业务冲突）
    this.CACHE_KEY = 'track_device_id_fingerprint'
    // 内存缓存（localStorage禁用时兜底）
    this.memoryCache = null
    // 校验 js-md5 是否已引入
    if (!md5) {
      console.warn('js-md5 未正确引入，指纹生成可能异常')
    }
  }

  /**
   * 核心方法：获取埋点用的deviceId
   * @returns {string} 32位MD5格式的设备指纹（唯一标识）
   */
  getDeviceId() {
    // 1. 优先读缓存（localStorage > 内存）
    const cacheId = this.getCache()
    if (cacheId) return cacheId

    // 2. 缓存未命中，生成指纹
    const fingerprint = this.generateFingerprint()

    // 3. 写入缓存（兼容隐私模式降级）
    this.setCache(fingerprint)

    return fingerprint
  }

  /**
   * 生成轻量浏览器指纹（仅收集非敏感特征）
   */
  generateFingerprint() {
    const nav = window.navigator || {}
    const scr = window.screen || {}

    // 收集高稳定性、非敏感的设备/浏览器特征
    const features = [
      // 浏览器核心信息
      nav.userAgent || '', // 浏览器+系统版本（如 Chrome/120 Windows）
      nav.language || '', // 浏览器语言（如 zh-CN）
      nav.platform || '', // 操作系统平台（Win32/MacIntel）
      // 设备屏幕信息
      `${scr.width || ''}x${scr.height || ''}`, // 屏幕分辨率
      scr.colorDepth || '', // 屏幕颜色深度
      // 浏览器能力（仅判断有无，不获取具体内容）
      nav.cookieEnabled ? 'cookie_on' : 'cookie_off',
      window.localStorage ? 'ls_on' : 'ls_off',
      (nav.plugins || []).length, // 插件数量（仅数量，不获取名称）
      // 时区与渲染特征（提升唯一性）
      new Date().getTimezoneOffset(), // 时区偏移量
      this.getCanvasFingerprint(), // Canvas轻量特征（无敏感计算）
    ]

    // 特征拼接后调用 js-md5 生成32位哈希（核心修改点）
    const featureStr = features.join('|')
    return md5 ? md5(featureStr) : this.fallbackMd5(featureStr)
  }

  /**
   * Canvas轻量指纹（提升唯一性，无视觉展示）
   */
  getCanvasFingerprint() {
    try {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      // 绘制简单图形（仅用于提取渲染特征）
      ctx.fillStyle = '#f8f8f8'
      ctx.fillRect(0, 0, 100, 100)
      // 仅取前32位，减少计算量
      return canvas.toDataURL().substring(0, 32)
    } catch (e) {
      // 无Canvas支持时降级
      return 'canvas_unsupported'
    }
  }

  /**
   * 降级方案：js-md5 加载失败时的极简MD5（仅兜底，不推荐依赖）
   * @param {string} str 特征字符串
   * @returns {string} 简易哈希值
   */
  fallbackMd5(str) {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i)
      hash &= hash // 转为32位整数
    }
    return Math.abs(hash).toString(16).padStart(32, '0')
  }

  /**
   * 读取缓存（localStorage + 内存降级）
   */
  getCache() {
    // 1. 优先读localStorage
    try {
      const cache = localStorage.getItem(this.CACHE_KEY)
      if (cache) return cache
    } catch (e) {
      // eslint-disable-next-line no-empty
    }

    // 2. 降级读内存缓存（隐私模式）
    return this.memoryCache
  }

  /**
   * 写入缓存（localStorage + 内存降级）
   * @param {string} fingerprint 生成的指纹
   */
  setCache(fingerprint) {
    // 1. 优先写localStorage
    try {
      localStorage.setItem(this.CACHE_KEY, fingerprint)
    } catch (e) {
      // 2. 隐私模式下写入内存
      this.memoryCache = fingerprint
    }
  }

  /**
   * 清除缓存（可选，如用户登出时）
   */
  clearCache() {
    try {
      localStorage.removeItem(this.CACHE_KEY)
    } catch (e) {
      // eslint-disable-next-line no-empty
    }
    this.memoryCache = null
  }
}
